// Client-side sniffer that decides whether an attachment should be
// auto-routed to the doc-import flow (treating it as a flow-grammar
// table) or treated as a regular chat attachment.
//
// Two-gate test for markdown / txt files:
//   1. At least one well-formed markdown table exists (header + divider
//      + MIN_DATA_ROWS data rows in a contiguous block).
//   2. Table rows make up at least MIN_TABLE_LINE_RATIO of the non-empty
//      lines in the document. This is what distinguishes a flow-import
//      table file (almost entirely table) from prose that happens to
//      contain a few tables.
//
// CSV / TSV by extension uses a simpler shape check (header + multiple
// data rows; column count >= 2).
//
// Anything that's clearly not text (images, PDFs) skips the probe.

const STRUCTURED_EXTENSIONS = ['.csv', '.tsv'];
const MAYBE_STRUCTURED_EXTENSIONS = ['.md', '.markdown', '.txt'];
const PROBE_BYTES = 32 * 1024;
const MARKDOWN_TABLE_LINE = /^\s*\|.+\|\s*$/;
const MARKDOWN_DIVIDER = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const MIN_DATA_ROWS = 3;

// A flow-import file is ~entirely a single table. Prose-with-tables
// documents like process-description / spec narratives drop well
// below 0.6 (typically 0.2–0.4). 0.6 leaves plenty of headroom for
// preambles / footers around a real table without bringing in noise.
const MIN_TABLE_LINE_RATIO = 0.6;

export interface StructuredDocSniffResult {
  isStructured: boolean;
  text: string;
}

export async function sniffStructuredDoc(file: File): Promise<StructuredDocSniffResult> {
  const name = (file.name || '').toLowerCase();
  const hasUnambiguousExt = STRUCTURED_EXTENSIONS.some((e) => name.endsWith(e));
  const hasMaybeExt = MAYBE_STRUCTURED_EXTENSIONS.some((e) => name.endsWith(e));
  if (!hasUnambiguousExt && !hasMaybeExt) return { isStructured: false, text: '' };

  let text: string;
  try {
    text = await file.text();
  } catch {
    return { isStructured: false, text: '' };
  }
  return { isStructured: classifyText(text, name), text };
}

/** Pure synchronous classifier — used internally by the sniffer and by tests. */
export function classifyText(text: string, lowerName: string): boolean {
  const hasUnambiguousExt = STRUCTURED_EXTENSIONS.some((e) => lowerName.endsWith(e));
  const hasMaybeExt = MAYBE_STRUCTURED_EXTENSIONS.some((e) => lowerName.endsWith(e));
  if (!hasUnambiguousExt && !hasMaybeExt) return false;

  if (hasUnambiguousExt) {
    const sample = text.split(/\r?\n/, 20).filter((l) => l.trim());
    const sep = lowerName.endsWith('.tsv') ? '\t' : ',';
    if (sample.length < 1 + MIN_DATA_ROWS) return false;
    const counts = sample.map((l) => l.split(sep).length);
    const max = Math.max(...counts);
    return max >= 2;
  }

  // Markdown / txt: must contain at least one valid table block AND have
  // a high ratio of table rows to total non-empty lines.
  const probe = text.length > PROBE_BYTES ? text.slice(0, PROBE_BYTES) : text;
  const probeLines = probe.split(/\r?\n/);
  const hasValidTable = findValidTable(probeLines);
  if (!hasValidTable) return false;

  // Ratio is computed over the WHOLE file (not just probe) so a giant
  // prose document doesn't get classified as structured just because
  // the first 32 KB happens to be table-heavy.
  const allLines = text.split(/\r?\n/);
  let nonEmpty = 0;
  let tableLines = 0;
  for (const line of allLines) {
    if (!line.trim()) continue;
    nonEmpty += 1;
    if (MARKDOWN_TABLE_LINE.test(line) || MARKDOWN_DIVIDER.test(line)) tableLines += 1;
  }
  if (nonEmpty === 0) return false;
  return (tableLines / nonEmpty) >= MIN_TABLE_LINE_RATIO;
}

function findValidTable(lines: string[]): boolean {
  for (let i = 0; i < lines.length - 1; i++) {
    if (!MARKDOWN_TABLE_LINE.test(lines[i])) continue;
    if (!lines[i + 1].includes('|')) continue;
    if (!MARKDOWN_DIVIDER.test(lines[i + 1])) continue;
    let dataRows = 0;
    for (let j = i + 2; j < lines.length; j++) {
      if (!MARKDOWN_TABLE_LINE.test(lines[j])) break;
      dataRows += 1;
      if (dataRows >= MIN_DATA_ROWS) return true;
    }
  }
  return false;
}
